import {useEffect, useState} from 'react';
import {NavBar} from 'components/NavBar';
import {About} from 'sections/About';
import {Contact} from 'sections/Contact';
import {Hero} from 'sections/Hero';
import {Projects} from 'sections/Projects';
import {Skills} from 'sections/Skills';
import {Work} from 'sections/Work';
import {LinkItem, Section} from 'types';

const navItems: Section[] = [
  {id: 'work', name: 'Work'},
  {id: 'projects', name: 'Projects'},
  {id: 'skills', name: 'Skills'},
  {id: 'about', name: 'About'},
  {id: 'contact', name: 'Contact'}
];

const socialLinks: LinkItem[] = [
  {label: 'GitHub', href: 'https://github.com/bshreddy', icon: 'github'},
  {label: 'LinkedIn', href: 'https://www.linkedin.com/in/bshreddy/', icon: 'linkedin'}
];

const footerLinks: LinkItem[] = [
  {label: 'Back to top', href: '#top'},
  {label: 'Resume', href: '/files/Resume.pdf'}
];

type ThemeName = 'light' | 'dark';

const themeStorageKey = 'theme';

function getSystemTheme(): ThemeName {
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
}

function getStoredTheme(): ThemeName | null {
  const storedTheme = window.localStorage.getItem(themeStorageKey);

  if (storedTheme === 'light' || storedTheme === 'dark') {
    return storedTheme;
  }

  return null;
}

function App() {
  const [theme, setTheme] = useState<ThemeName>(() => getStoredTheme() ?? getSystemTheme());
  const [manualTheme, setManualTheme] = useState(() => getStoredTheme() !== null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;

    if (manualTheme) {
      window.localStorage.setItem(themeStorageKey, theme);
    }
  }, [manualTheme, theme]);

  useEffect(() => {
    if (manualTheme) {
      return undefined;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      setTheme(event.matches ? 'dark' : 'light');
    };

    media.addEventListener('change', handleSystemThemeChange);

    return () => {
      media.removeEventListener('change', handleSystemThemeChange);
    };
  }, [manualTheme]);

  const toggleTheme = () => {
    setManualTheme(true);
    setTheme((currentTheme) => currentTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="app">
      <NavBar
        sections={navItems}
        socialLinks={socialLinks}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      <main id="top">
        <section className="section hero-section" id="hero">
          <Hero />
        </section>

        <section className="section" id="work">
          <Work />
        </section>

        <section className="section" id="projects">
          <Projects />
        </section>

        <section className="section" id="skills">
          <Skills />
        </section>

        <section className="section" id="about">
          <About />
        </section>

        <section className="section contact-section" id="contact">
          <Contact />
        </section>
      </main>

      <footer className="site-footer">
        <div className="section-inner footer-inner">
          <p>© 2026 Sai Hemanth Bheemreddy</p>
          <div className="footer-links">
            {footerLinks.map((link) => (
              <a key={link.href} href={link.href}>{link.label}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
